import React, { useEffect, useState } from "react";
import { UploadCloud, Trash2 } from "lucide-react";
import toast from "react-hot-toast";
import { AppModalFooter, AppModalShell } from "./app-ui.js";
import { Button, Input } from "./legacy-ui.js";
import { useAuth } from "../context/AuthContext.js";

const UserAvatar = ({ user, size = "md", className = "" }) => {
  const sizes = {
    sm: "w-8 h-8 text-xs rounded-lg",
    md: "w-10 h-10 text-sm rounded-xl",
    lg: "w-24 h-24 text-2xl rounded-2xl",
  };
  const name = user?.fullName || user?.username || "?";
  const initial = name.charAt(0).toUpperCase();
  if (user?.avatarUrl) {
    return (
      <img
        src={user.avatarUrl}
        alt={name}
        className={`${sizes[size] || sizes.md} object-cover bg-slate-700 flex-shrink-0 ${className}`}
      />
    );
  }
  return (
    <div
      className={`${sizes[size] || sizes.md} bg-indigo-600 bg-opacity-80 flex items-center justify-center font-bold text-white flex-shrink-0 ${className}`}
    >
      {initial}
    </div>
  );
};

export function ProfileSettingsModal({ open, onClose }) {
  const { user, updateProfile } = useAuth();
  const [fullName, setFullName] = useState("");
  const [preview, setPreview] = useState("");
  const [file, setFile] = useState(null);
  const [removeAvatar, setRemoveAvatar] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !user) return;
    setFullName(user.fullName || "");
    setPreview(user.avatarUrl || "");
    setFile(null);
    setRemoveAvatar(false);
  }, [open, user]);

  const handleFile = (event) => {
    const next = event.target.files?.[0];
    if (!next) return;
    if (!next.type.startsWith("image/")) {
      toast.error("Selecciona una imagen", { position: "top-center" });
      return;
    }
    setFile(next);
    setRemoveAvatar(false);
    setPreview(URL.createObjectURL(next));
  };

  const handleRemovePhoto = () => {
    setFile(null);
    setPreview("");
    setRemoveAvatar(Boolean(user?.avatarUrl));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const name = fullName.trim();
    if (!name) {
      toast.error("El nombre es obligatorio", { position: "top-center" });
      return;
    }
    const form = new FormData();
    form.append("fullName", name);
    if (file) form.append("avatar", file);
    if (removeAvatar && !file) form.append("removeAvatar", "true");

    setSaving(true);
    try {
      await updateProfile(form);
      toast.success("Perfil actualizado", { position: "top-center" });
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.error || "No se pudo guardar el perfil", {
        position: "top-center",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <AppModalShell
      open={open}
      onClose={onClose}
      title="Configuración del perfil"
      widthClass="max-w-md"
      fitContent
      footer={
        <AppModalFooter>
          <Button type="button" variant="secondary" className="w-full sm:w-auto" onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          <Button type="submit" form="profile-settings-form" className="w-full sm:w-auto" disabled={saving}>
            {saving ? "Guardando…" : "Guardar"}
          </Button>
        </AppModalFooter>
      }
    >
      <form id="profile-settings-form" onSubmit={handleSubmit} className="space-y-4">
        <div className="flex flex-col items-center gap-3">
          {preview ? (
            <img src={preview} alt="Foto de perfil" className="w-24 h-24 rounded-2xl object-cover border border-gray-200" />
          ) : (
            <UserAvatar user={{ ...user, fullName, avatarUrl: "" }} size="lg" />
          )}
          <div className="flex flex-wrap items-center justify-center gap-2">
            <label className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-indigo-700 bg-indigo-50 rounded-lg cursor-pointer hover:bg-indigo-100">
              <UploadCloud size={16} />
              Cambiar foto
              <input type="file" accept="image/*" className="hidden" onChange={handleFile} />
            </label>
            {(preview || user?.avatarUrl) && (
              <button
                type="button"
                className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-red-600 bg-red-50 rounded-lg hover:bg-red-100"
                onClick={handleRemovePhoto}
              >
                <Trash2 size={16} />
                Quitar
              </button>
            )}
          </div>
        </div>

        <Input
          label="Nombre"
          value={fullName}
          onChange={(e) => setFullName(e.target.value)}
          placeholder="Tu nombre"
          required
        />
        <Input label="Usuario" value={user?.username || ""} readOnly className="bg-gray-50 text-gray-500" />
      </form>
    </AppModalShell>
  );
}

export default UserAvatar;
